import React from "react";
import { Link } from "react-router-dom";

/**
 * SocialIcon — Iteration 32 (+ iter-60 sizing normalisation)
 * Each platform uses its OFFICIAL brand color (or gradient). The container
 * is a fixed 36×36px circle and every glyph SVG is rendered at the SAME
 * 16×16 box with `block` (no inline baseline offset) + `flex-shrink-0`
 * so all icons share the exact same visual weight regardless of how dense
 * the underlying path data is.
 */
const SocialIcon = ({ label, href, testid, brandClass = "", style, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    data-testid={testid}
    style={style}
    className={`w-9 h-9 shrink-0 rounded-full bg-white border border-[#E5E1D8] flex items-center justify-center transition-transform hover:-translate-y-0.5 ${brandClass}`}
  >
    {children}
  </a>
);

const Footer = () => (
  <footer className="mt-24 border-t border-[#E5E1D8] bg-[#F6F4EE]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 grid md:grid-cols-4 gap-8 text-[#5E5C58] text-sm">
      <div>
        <div className="font-heading text-xl text-[#222222]">أوتادك</div>
        <p
          data-testid="footer-tagline"
          className="mt-3 font-heading text-lg text-[#0A4D3C] leading-relaxed"
        >
          أوتادٌ ثابتة.. لحياةٍ مطمئنة
        </p>
      </div>
      <div data-testid="footer-consultant" className="md:text-center flex md:justify-center items-start">
        <img
          src="https://customer-assets.emergentagent.com/job_counsel-marketplace/artifacts/60nvz0lu_%D8%A3%D9%86%D8%A7%20%D8%A7%D9%84%D8%AE%D8%A7%D9%84%D8%AF%D9%8A%20%28%D8%A7%D9%84%D9%84%D9%88%D9%82%D9%88%20%D8%A7%D9%84%D9%86%D9%87%D8%A7%D8%A6%D9%8A%29.png"
          alt="أنا الخالدي"
          className="h-24 sm:h-28 w-auto object-contain select-none"
          draggable="false"
        />
      </div>
      <div data-testid="footer-socials">
        <div className="text-[#0A4D3C] tracking-widest text-xs">تواصل اجتماعي</div>
        <div className="mt-3 flex items-center gap-2 flex-wrap" dir="ltr">
          {/* WhatsApp — official #25D366 */}
          <SocialIcon
            label="WhatsApp +966 53 827 8484"
            href="https://wa.me/966538278484"
            testid="footer-whatsapp"
            style={{ color: "#25D366" }}
          >
            <svg viewBox="0 0 24 24" className="w-[14px] h-[14px] block" fill="currentColor">
              <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.55 4.15 1.6 5.96L2 22l4.24-1.11a9.86 9.86 0 0 0 5.8 1.85c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm5.78 14.07c-.24.68-1.41 1.34-1.95 1.39-.5.05-1.13.07-1.83-.11-.42-.13-.97-.31-1.66-.6-2.93-1.27-4.84-4.22-4.99-4.42-.15-.2-1.18-1.57-1.18-2.99 0-1.42.74-2.12 1-2.41.27-.29.58-.36.78-.36.2 0 .4 0 .57.01.18.01.43-.07.67.51.24.58.83 2.01.9 2.16.07.15.12.32.02.51-.1.2-.15.32-.29.5-.15.18-.31.4-.44.54-.15.15-.3.31-.13.61.18.29.78 1.29 1.67 2.09 1.15 1.03 2.13 1.35 2.42 1.5.29.15.46.13.63-.07.18-.2.73-.85.92-1.14.18-.29.37-.24.62-.15.25.1 1.6.75 1.87.89.27.13.45.2.52.31.07.12.07.68-.17 1.36z" />
            </svg>
          </SocialIcon>

          {/* Instagram — official brand gradient */}
          <SocialIcon
            label="Instagram analkhaldi70"
            href="https://www.instagram.com/analkhaldi70"
            testid="footer-instagram"
            style={{ color: "#FFFFFF", background:
              "radial-gradient(circle at 30% 110%, #FFD776 0%, #F58529 25%, #DD2A7B 50%, #8134AF 75%, #515BD4 100%)",
              borderColor: "transparent" }}
          >
            <svg viewBox="0 0 24 24" className="w-[14px] h-[14px] block" fill="currentColor">
              <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.43.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38a3.73 3.73 0 0 1-1.38.9c-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.73 3.73 0 0 1-1.38-.9 3.73 3.73 0 0 1-.9-1.38c-.16-.43-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 1.94c-3.15 0-3.5.01-4.74.07-1.07.05-1.65.23-2.04.38-.51.2-.88.44-1.27.83a3.42 3.42 0 0 0-.83 1.27c-.15.39-.33.97-.38 2.04-.06 1.24-.07 1.59-.07 4.74s.01 3.5.07 4.74c.05 1.07.23 1.65.38 2.04.2.51.44.88.83 1.27.39.39.76.63 1.27.83.39.15.97.33 2.04.38 1.24.06 1.59.07 4.74.07s3.5-.01 4.74-.07c1.07-.05 1.65-.23 2.04-.38.51-.2.88-.44 1.27-.83.39-.39.63-.76.83-1.27.15-.39.33-.97.38-2.04.06-1.24.07-1.59.07-4.74s-.01-3.5-.07-4.74c-.05-1.07-.23-1.65-.38-2.04a3.42 3.42 0 0 0-.83-1.27 3.42 3.42 0 0 0-1.27-.83c-.39-.15-.97-.33-2.04-.38-1.24-.06-1.59-.07-4.74-.07zm0 3.32a4.58 4.58 0 1 1 0 9.16 4.58 4.58 0 0 1 0-9.16zm0 1.94a2.64 2.64 0 1 0 0 5.28 2.64 2.64 0 0 0 0-5.28zm5.83-2.16a1.07 1.07 0 1 1-2.14 0 1.07 1.07 0 0 1 2.14 0z" />
            </svg>
          </SocialIcon>

          {/* Snapchat — official #FFFC00 (yellow) with dark glyph for contrast */}
          <SocialIcon
            label="Snapchat analkhaldi70"
            href="https://www.snapchat.com/add/analkhaldi70"
            testid="footer-snapchat"
            style={{ background: "#FFFC00", color: "#222222", borderColor: "#E6D900" }}
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 block" fill="currentColor">
              <path d="M12.055 1.34c2.574 0 4.672 2.097 4.672 4.672v1.88c.02.247.046.484.08.71.25.08.67.133 1.02.133.56 0 .93-.14 1.18-.36.17-.14.36-.24.6-.24.55 0 1.02.45 1.02.96 0 .42-.28.73-.77.99-.41.22-1.06.36-1.68.5-.18.04-.36.08-.54.13 0 .39.12.78.34 1.2.24.46.63.96 1.24 1.56.46.46 1 .76 1.73 1.05.35.14.7.25.7.6 0 .53-.95.93-1.97 1.13-.16.03-.3.05-.4.11-.1.07-.16.18-.2.32-.1.3-.19.66-.33.9-.13.23-.3.33-.64.33-.26 0-.6-.08-1.02-.16-.44-.08-.95-.18-1.56-.18-.62 0-1.05.22-1.55.53-.6.37-1.28.8-2.47.8s-1.87-.43-2.47-.8c-.5-.31-.93-.53-1.55-.53-.61 0-1.12.1-1.56.18-.42.08-.76.16-1.02.16-.34 0-.51-.1-.64-.33-.14-.24-.23-.6-.33-.9-.04-.14-.1-.25-.2-.32-.1-.06-.24-.08-.4-.11C3.95 14.52 3 14.12 3 13.59c0-.35.35-.46.7-.6.73-.29 1.27-.59 1.73-1.05.61-.6 1-1.1 1.24-1.56.22-.42.34-.81.34-1.2-.18-.05-.36-.09-.54-.13-.62-.14-1.27-.28-1.68-.5-.49-.26-.77-.57-.77-.99 0-.51.47-.96 1.02-.96.24 0 .43.1.6.24.25.22.62.36 1.18.36.35 0 .77-.053 1.02-.133.034-.226.06-.463.08-.71V6.012c0-2.575 2.098-4.672 4.672-4.672z" />
            </svg>
          </SocialIcon>

          {/* TikTok — official black with brand red/teal accent fills */}
          <SocialIcon
            label="TikTok analkhaldi70"
            href="https://www.tiktok.com/@analkhaldi70"
            testid="footer-tiktok"
            style={{ background: "#000000", color: "#FFFFFF", borderColor: "transparent" }}
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 block" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-5 .97 6.27 6.27 0 0 0-2.78 5.3 6.34 6.34 0 0 0 10.86 4.42 6.28 6.28 0 0 0 1.87-4.48v-7a8.16 8.16 0 0 0 4.77 1.52v-3.44a4.85 4.85 0 0 1-.49-.02z" />
            </svg>
          </SocialIcon>
        </div>
        <div className="mt-3 text-xs text-[#5E5C58]">
          يتم الإعلان عن مواعيد البث عبر Snapchat.
        </div>
      </div>
      <div className="md:text-end">
        <div className="text-[#0A4D3C] tracking-widest text-xs">تواصل</div>
        <div
          className="mt-2 text-[13px] text-[#222222] font-medium"
          data-testid="footer-whatsapp-label"
        >
          للتواصل مع خدمة العملاء
        </div>
        <a
          href="https://wa.me/966538278484"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-block text-[#25D366] hover:text-[#1ebe5b]"
          dir="ltr"
          data-testid="footer-whatsapp-link"
        >
          +966 53 827 8484
        </a>
        <div className="mt-3 flex flex-col gap-1.5">
          <Link to="/terms" data-testid="footer-terms-link" className="text-[#0A4D3C] underline">
            الشروط والتعليمات
          </Link>
          <Link
            to="/refund-policy"
            data-testid="footer-refund-policy-link"
            className="text-[#0A4D3C] underline"
          >
            سياسة الشراء والاسترجاع
          </Link>
          <Link
            to="/terms-and-privacy"
            data-testid="footer-terms-privacy-link"
            className="text-[#0A4D3C] underline"
          >
            اتفاقية الاستخدام والخصوصية
          </Link>
        </div>
        <div className="mt-3 text-xs">© {new Date().getFullYear()} جميع الحقوق محفوظة.</div>
      </div>
    </div>
  </footer>
);

export default Footer;
